import { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid';
import { advancedFilterModelToGridFilterModel } from '@nohara-dxdev/react-core';
import { filterModelToRSQL, rsqlBuilder, serializeRSQL } from '@nohara-dxdev/rsql';

import type { ItemFilterValues } from '../providers';
import type { CustomExpressionNode } from '@nohara-dxdev/rsql';

/**
 * The system column filters by name for text operators, but "empty"/"not empty"
 * means "has no construction type assigned" — so those operators target
 * constructionTypeId instead of systems.name.
 */
function remapSystemEmptyField(filterModel: GridFilterModel): GridFilterModel {
  return {
    ...filterModel,
    items: filterModel.items.map((item) =>
      item.field === 'systems.name' &&
      (item.operator === 'isEmpty' || item.operator === 'isNotEmpty')
        ? { ...item, field: 'constructionTypeId' }
        : item,
    ),
  };
}

/**
 * Convert ItemFilterValues to API query parameters
 *
 * This function:
 * 1. Converts filterModel to RSQL string
 * 2. Combines with excludeIds using OUT operator (id=out=(...))
 * 3. Handles special params (locationIdOrAdjacentLocationIdEq, infrastructureRelationshipRequirements)
 * 4. Maps to backend API contract
 */
export function convertItemFilterToApiParams(
  filterValues: ItemFilterValues,
): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  // Build RSQL expressions to combine
  const rsqlExpressions: CustomExpressionNode[] = [];

  // Prioritize advancedFilterModel over filterModel if both are present
  if (filterValues.advancedFilterModel && filterValues.advancedFilterModel.conditions.length > 0) {
    // Handle advancedFilterModel (converts to filterModel format)
    try {
      const gridFilterModel = advancedFilterModelToGridFilterModel(
        filterValues.advancedFilterModel,
      );
      if (gridFilterModel.items.length > 0) {
        const filterModelAST = filterModelToRSQL([
          remapSystemEmptyField(gridFilterModel as GridFilterModel),
        ]);
        if (filterModelAST) {
          rsqlExpressions.push(filterModelAST);
        }
      }
    } catch (error) {
      console.error('Failed to convert advancedFilterModel to RSQL AST:', error);
    }
  } else if (filterValues.filterModel && filterValues.filterModel.length > 0) {
    // Convert filterModel array to RSQL AST (fallback for backward compatibility)
    // Each filterModel in the array is treated as an AND group
    try {
      const filterModelAST = filterModelToRSQL(filterValues.filterModel.map(remapSystemEmptyField));
      if (filterModelAST) {
        rsqlExpressions.push(filterModelAST);
      }
    } catch (error) {
      console.error('Failed to convert filterModel array to RSQL AST:', error);
    }
  }

  // Handle excludeIds with OUT operator (id=out=(...))
  if (filterValues.excludeIds && filterValues.excludeIds.length > 0) {
    rsqlExpressions.push(rsqlBuilder.out('id', filterValues.excludeIds));
  }

  // Combine all RSQL expressions into a single RSQL string
  let rsqlFilter: string | undefined;
  if (rsqlExpressions.length > 0) {
    if (rsqlExpressions.length === 1) {
      rsqlFilter = serializeRSQL(rsqlExpressions[0]);
    } else {
      rsqlFilter = serializeRSQL(rsqlBuilder.and(...rsqlExpressions));
    }
  }

  // Add RSQL filter if present
  if (rsqlFilter) {
    params.rsqlFilter = rsqlFilter;
  }

  // Add special params
  if (filterValues.locationIdOrAdjacentLocationIdEq) {
    params.locationIdOrAdjacentLocationIdEq = filterValues.locationIdOrAdjacentLocationIdEq;
  }

  if (
    filterValues.infrastructureRelationshipRequirements &&
    filterValues.infrastructureRelationshipRequirements.length > 0
  ) {
    params.infrastructureRelationshipRequirements =
      filterValues.infrastructureRelationshipRequirements;
  }

  // Add nameOrItemNumberMatch param (full-text search)
  if (filterValues.nameOrItemNumberMatch) {
    params.nameOrItemNumberMatch = filterValues.nameOrItemNumberMatch;
  }

  // Add sorting params
  if (filterValues.sortField) {
    params.sortField = filterValues.sortField;
  }
  if (filterValues.sortOrder) {
    params.sortOrder = filterValues.sortOrder;
  }

  // Passed as a string because query params are always strings; the backend parses it.
  if (filterValues.isUnassignedToLocation) {
    params.isUnassignedToLocation = 'true';
  }

  if (filterValues.isUnassignedToSystem) {
    params.isUnassignedToSystem = 'true';
  }

  return params;
}

/**
 * Convert legacy filter fields to new ItemFilterValues format
 * This helper converts old individual filter fields to the new filterModel-based format
 */
export function convertLegacyFieldsToFilterValues(fields: {
  constructionTypeId?: string;
  systemIds?: string[];
  itemTypeIds?: string[];
  floorId?: string;
  buildingId?: string;
  roomId?: string; // Deprecated - use locationIdOrAdjacentLocationIdEq instead
  locationIdOrAdjacentLocationIdEq?: string;
  name?: string;
  excludeIds?: string[];
  projectRoleTags?: string[];
  infrastructureRelationshipRequirements?: string[];
}): ItemFilterValues {
  const filterModelItems: GridFilterModel['items'] = [];

  // Convert individual fields to filterModel items
  if (fields.constructionTypeId) {
    filterModelItems.push({
      field: 'constructionTypeId',
      operator: 'equals',
      value: fields.constructionTypeId,
    });
  }

  if (fields.systemIds && fields.systemIds.length > 0) {
    filterModelItems.push({
      field: 'systemIds',
      operator: 'contains',
      value: fields.systemIds,
    });
  }

  if (fields.itemTypeIds && fields.itemTypeIds.length > 0) {
    filterModelItems.push({
      field: 'itemTypeId',
      operator: 'isAnyOf',
      value: fields.itemTypeIds,
    });
  }

  if (fields.floorId) {
    filterModelItems.push({
      field: 'floorId',
      operator: 'equals',
      value: fields.floorId,
    });
  }

  if (fields.buildingId) {
    filterModelItems.push({
      field: 'buildingId',
      operator: 'equals',
      value: fields.buildingId,
    });
  }

  if (fields.name) {
    filterModelItems.push({
      field: 'name',
      operator: 'contains',
      value: fields.name,
    });
  }

  if (fields.projectRoleTags && fields.projectRoleTags.length > 0) {
    filterModelItems.push({
      field: 'projectRoleTags',
      operator: 'isAnyOf',
      value: fields.projectRoleTags,
    });
  }

  const result: ItemFilterValues = {};

  // Add filterModel array if there are any items
  // Each filterModel is treated as an AND group
  if (filterModelItems.length > 0) {
    result.filterModel = [
      {
        items: filterModelItems,
        logicOperator: GridLogicOperator.And,
      },
    ];
  }

  // Handle excludeIds separately (uses OUT operator)
  if (fields.excludeIds && fields.excludeIds.length > 0) {
    result.excludeIds = fields.excludeIds;
  }

  // Handle locationIdOrAdjacentLocationIdEq
  if (fields.locationIdOrAdjacentLocationIdEq) {
    result.locationIdOrAdjacentLocationIdEq = fields.locationIdOrAdjacentLocationIdEq;
  }

  // Legacy: Handle roomId -> locationIdOrAdjacentLocationIdEq conversion (for backward compatibility during transition)
  if (fields.roomId && !fields.locationIdOrAdjacentLocationIdEq) {
    result.locationIdOrAdjacentLocationIdEq = fields.roomId;
  }

  // Handle infrastructureRelationshipRequirements
  if (
    fields.infrastructureRelationshipRequirements &&
    fields.infrastructureRelationshipRequirements.length > 0
  ) {
    result.infrastructureRelationshipRequirements =
      fields.infrastructureRelationshipRequirements as ItemFilterValues['infrastructureRelationshipRequirements'];
  }

  return result;
}
